import type { IntlShape } from 'react-intl'
import { DEFAULT_REVIEWS_TOTAL_THRESHOLD, RECOMMENDED_RATING_THRESHOLD } from 'constants/review'
import { isOrderPast } from 'lib/order/isUpcoming'

export function checkOrderAcceptsReview(order?: App.Order) {
  if (!order) return false

  const isTravelledOrder = isOrderPast(order)
  const isValidOrderType = !!(order.items.length || order.tourItems.length || order.experienceItems.length || order.bedbankItems.length)
  const isCompletedItem = isOrderItemCompleted(order)

  return isTravelledOrder && isValidOrderType && isCompletedItem
}

/**
 * For now we're only getting the first item inside order to leave reviews
 * So we get only 1 review by order. For this reason, we check status of the first item
 */
export function isOrderItemCompleted(order: App.Order): boolean {
  return !!order.items.length && order.items[0].status === 'completed' ||
    !!order.bedbankItems.length && order.bedbankItems[0].status === 'completed' ||
    !!order.tourItems.length && order.tourItems[0].status === 'completed' ||
    !!order.experienceItems.length && order.experienceItems[0].status === 'completed'
}

export function isOfferRatingDisplayable(
  rating: App.OfferRating | undefined,
  /**
   * Minimum number of reviews required on a rating to be displayed.
   */
  reviewsTotalThreshold: number = DEFAULT_REVIEWS_TOTAL_THRESHOLD,
): rating is App.OfferRating {
  if (!rating) return false
  return !!rating.score && rating.reviewsTotal >= reviewsTotalThreshold
}

export function isExperienceOfferRatingDisplayable(experienceOffer: App.ExperienceOffer | App.ExperienceBookingView): boolean {
  return !!experienceOffer.rating?.score && !experienceOffer.disableReviews
}

export const getRecommendsLabel = (review: App.Review, intl: IntlShape) => {
  const isRecommended = review.rating >= RECOMMENDED_RATING_THRESHOLD

  if (review.experienceId) {
    return isRecommended ? intl.formatMessage({
      id: 'xMalltHs-1j8CohvT8Br',
      defaultMessage: 'Recommends this experience',
      description: 'Review label indicating the reviewer recommends this experience to others',
    }) : intl.formatMessage({
      id: 'aXudOaY2TNqfC4NRl-Jm',
      defaultMessage: 'Does not recommend this experience',
      description: 'Review label indicating the reviewer does not recommend this experience',
    })
  }

  if (review.tourId) {
    return isRecommended ? intl.formatMessage({
      id: 'HpnG51sEG_9mNG5-f3gY',
      defaultMessage: 'Recommends this tour',
      description: 'Review label indicating the reviewer recommends this tour to other travellers',
    }) : intl.formatMessage({
      id: 'vB4z0a5uWdwZMQAmUsEc',
      defaultMessage: 'Does not recommend this tour',
      description: 'Review label indicating the reviewer does not recommend this tour to others',
    })
  }

  return isRecommended ? intl.formatMessage({
    id: 'jgHPnGx0HChhd8XLeoN1',
    defaultMessage: 'Recommends this hotel',
    description: 'Review label indicating the reviewer recommends this hotel to other travellers',
  }) : intl.formatMessage({
    id: 'YH4L1gG4uh8j8v_F3wPa',
    defaultMessage: 'Does not recommend this hotel',
    description: 'Review label indicating the reviewer does not recommend this hotel to others',
  })
}
